import { motion } from "framer-motion";
import { Network, Archive, Folders, ShieldCheck, ArrowUpRight } from "@phosphor-icons/react";

const CABLES_IMAGE =
  "https://images.unsplash.com/photo-1683322499436-f4383dd59f5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMG5ldHdvcmslMjBzd2l0Y2glMjBjYWJsZXN8ZW58MHx8fHwxNzg0MjA1MjE4fDA&ixlib=rb-4.1.0&q=85";

const services = [
  {
    number: "01",
    icon: Network,
    title: "Gestão de Redes",
    description:
      "Monitoramento contínuo, Wi-Fi corporativo e infraestrutura estável. Sua operação conectada, sem quedas e sem improviso.",
    testid: "service-card-network",
    featured: true,
  },
  {
    number: "02",
    icon: Archive,
    title: "Inventário de Equipamentos",
    description:
      "Cada computador, servidor e dispositivo catalogado e rastreado. Você sabe o que tem, onde está e quando renovar.",
    testid: "service-card-inventory",
  },
  {
    number: "03",
    icon: Folders,
    title: "Organização de TI",
    description:
      "Senhas, acessos, licenças e processos documentados. O fim da dependência de 'uma pessoa que sabe tudo'.",
    testid: "service-card-organization",
  },
  {
    number: "04",
    icon: ShieldCheck,
    title: "Suporte Proativo",
    description:
      "Resolvemos antes de virar problema. Manutenção preventiva, backups verificados e resposta rápida quando você precisa.",
    testid: "service-card-support",
    dark: true,
  },
];

const cardReveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Services() {
  return (
    <section id="servicos" data-testid="services-section" className="py-24 lg:py-32 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 lg:mb-20 max-w-2xl"
        >
          <p data-testid="services-eyebrow" className="text-xs font-semibold tracking-[0.3em] uppercase text-gold-dark mb-6">
            Serviços
          </p>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.02]">
            Tudo no lugar.
            <span className="block text-gold-dark">Nada ao acaso.</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((s, i) => {
            const Icon = s.icon;
            const base = "group relative rounded-sm border p-8 lg:p-10 flex flex-col justify-between min-h-[320px] transition-all duration-500 hover:-translate-y-1";
            const theme = s.dark
              ? "bg-ink border-ink hover:border-gold text-[#FAFAFA] noise-overlay"
              : "bg-white border-black/5 hover:border-gold/60 text-ink";
            const span = s.featured ? "md:col-span-2 lg:col-span-2" : "";
            return (
              <motion.article
                key={s.number}
                custom={i}
                variants={cardReveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                className={`${base} ${theme} ${span}`}
                data-testid={s.testid}
              >
                {s.featured && (
                  <div className="absolute inset-0 overflow-hidden rounded-sm" aria-hidden="true">
                    <img
                      src={CABLES_IMAGE}
                      alt=""
                      className="w-full h-full object-cover opacity-[0.08] transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="relative flex items-start justify-between">
                  <span className={`font-display font-black text-5xl tracking-tighter ${s.dark ? "text-gold/40" : "text-gold/50"}`}>
                    {s.number}
                  </span>
                  <span className={`w-12 h-12 flex items-center justify-center rounded-sm border transition-colors duration-500 ${s.dark ? "border-gold/30 text-gold" : "border-black/10 text-gold-dark group-hover:bg-gold group-hover:text-ink group-hover:border-gold"}`}>
                    <Icon size={24} weight="duotone" />
                  </span>
                </div>
                <div className="relative mt-10">
                  <h3 className="font-display font-extrabold tracking-tight text-2xl mb-3 flex items-center gap-2">
                    {s.title}
                    <ArrowUpRight size={18} weight="bold" className="opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-500 text-gold-dark" />
                  </h3>
                  <p className={`text-sm leading-relaxed ${s.dark ? "text-neutral-400" : "text-neutral-600"}`}>
                    {s.description}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
